import type { FC } from 'react';

import { Divider } from '../divider';
import { PopoverArrow } from './popover-arrow';
import { PopoverBackdrop } from './popover-backdrop';
import { PopoverBody } from './popover-body';
import { PopoverClose } from './popover-close';
import { PopoverCloseButton } from './popover-close-button';
import { PopoverContent } from './popover-content';
import { PopoverHeader } from './popover-header';
import { PopoverPortal } from './popover-portal';
import { PopoverRoot } from './popover-root';
import { PopoverTrigger } from './popover-trigger';
import type { PopoverPropsInterface } from './types/popover.types';

/**
 * Popover is a Floating UI component for contextual content. It is similar to the Tooltip
 * component, but it does include a close button.
 *
 * @since 7.0.0
 */

export const Popover: FC<PopoverPropsInterface> & {
  Root: typeof PopoverRoot;
  Backdrop: typeof PopoverBackdrop;
  Trigger: typeof PopoverTrigger;
  Portal: typeof PopoverPortal;
  Arrow: typeof PopoverArrow;
  Content: typeof PopoverContent;
  Header: typeof PopoverHeader;
  Body: typeof PopoverBody;
  CloseButton: typeof PopoverCloseButton;
  Close: typeof PopoverClose;
} = (props: PopoverPropsInterface) => {
  const {
    children,
    dividers = true,
    trigger,
    header,
    body,
    closeButton,
    css: cssProp,
    className,
    style,
    portalId,
    portalRoot,
    portalPreserveTabOrder,
    ...rest
  } = props;
  return (
    <Popover.Root dividers={dividers} {...rest}>
      <Popover.Trigger>{trigger}</Popover.Trigger>
      <Popover.Portal id={portalId} root={portalRoot} preserveTabOrder={portalPreserveTabOrder}>
        <Popover.Backdrop>
          <Popover.Content css={cssProp} style={style} className={className}>
            {header ? <Popover.Header>{header}</Popover.Header> : null}
            {header && dividers ? <Divider /> : null}
            {body ? <Popover.Body>{body}</Popover.Body> : null}
            {children}
            <Popover.CloseButton>{closeButton}</Popover.CloseButton>
            <Popover.Arrow />
          </Popover.Content>
        </Popover.Backdrop>
      </Popover.Portal>
    </Popover.Root>
  );
};

Popover.displayName = 'Popover';

Popover.Root = PopoverRoot;
Popover.Backdrop = PopoverBackdrop;
Popover.Trigger = PopoverTrigger;
Popover.Portal = PopoverPortal;
Popover.Arrow = PopoverArrow;
Popover.Content = PopoverContent;
Popover.Header = PopoverHeader;
Popover.Body = PopoverBody;
Popover.CloseButton = PopoverCloseButton;
Popover.Close = PopoverClose;
